import { FC } from 'react';
import { useHistory, useParams } from 'react-router';
import { Flex, Image, Stack } from '@chakra-ui/react';

import { CardProductId, FeatureName } from '__generated__/GQL';
import { isCreditCardPendingOrder } from 'common/creditCards/isCreditCardPendingOrder';
import { formatAmount } from 'common/formatAmount';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import { getCardAssetSrc } from 'common/paymentCards/utils';
import ProgressBar from 'components/_core/ProgressBar';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import { useFakeTemporaryVirtualPaymentCardIdContext } from 'features/PaymentCards/context';

import { CreditCard } from '../../../../hooks/useCreditCards';
import * as routes from '../../../../routes';
import PaymentCardLimitsSkeleton from '../PaymentCardLimitsSkeleton';
import PaymentCardStatusBadge from '../PaymentCardStatusBadge';
import useCreditCardTotals from './hooks/useCreditCardTotals';
import locales from './locales';
import { getCardKey } from './utils';

interface PaymentCardRowParams {
  creditCardId: CreditCard['creditCardId'];
}

interface PaymentCardRowProps {
  creditCard: CreditCard;
  shouldDisplayCardOwnerName?: boolean;
}

const getCardDescription = ({
  cardLocales,
  formattedPan,
  isFakeVirtual,
}: {
  isFakeVirtual: boolean;
  formattedPan: string | null;
  cardLocales: (typeof locales)['cardWithName' | 'cardWithoutName'][ReturnType<
    typeof getCardKey
  >];
}) => {
  if (isFakeVirtual) {
    return locales.virtualCard;
  }
  return formattedPan
    ? locales.formatString(cardLocales.description, {
        pan: formattedPan,
      })
    : '';
};

const PaymentCardRow: FC<PaymentCardRowProps> = ({
  creditCard,
  shouldDisplayCardOwnerName,
}) => {
  const history = useHistory();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const fakeTemporaryVirtualPaymentCardId =
    useFakeTemporaryVirtualPaymentCardIdContext();

  const {
    alias,
    cardHolderName,
    cardProductId,
    creditCardId,
    isDefinitivelyBlocked,
    limitPaymentAll,
    limitPaymentWeek,
    totalPaymentAll,
    totalPaymentWeek,
  } = creditCard;
  const isBudgetCard = cardProductId && cardProductId === CardProductId.Budget;

  const { creditCardId: selectedCreditCardId } =
    useParams<PaymentCardRowParams>();

  // This query will update the creditCard data with new card totals, see apollo cache
  const { loading } = useCreditCardTotals({
    creditCardId,
    skip: creditCardId === fakeTemporaryVirtualPaymentCardId,
  });

  const cardAssetUrl = getCardAssetSrc(creditCard);

  const isSelected = creditCardId === selectedCreditCardId;
  const formattedPan = !isCreditCardPendingOrder(creditCard)
    ? creditCard.maskedPan.slice(-4)
    : null;

  const canAliasCard = canAccessFeature({
    name: FeatureName.Paymentcards,
  });

  const cardAlias = alias || (canAliasCard ? cardHolderName ?? '' : '');

  const cardLocales =
    locales[
      shouldDisplayCardOwnerName || cardAlias
        ? 'cardWithName'
        : 'cardWithoutName'
    ][getCardKey(creditCard)];

  const cardName = locales.formatString(cardLocales.name, {
    cardAlias,
  });

  const cardDescription = getCardDescription({
    cardLocales,
    formattedPan,
    isFakeVirtual: fakeTemporaryVirtualPaymentCardId === creditCardId,
  });

  const totalPayment = isBudgetCard ? totalPaymentAll : totalPaymentWeek;
  const limitPayment = isBudgetCard ? limitPaymentAll : limitPaymentWeek;

  return (
    <SunshineCard
      alignItems="center"
      border="1.5px solid"
      borderColor={isSelected ? 'card-borderColor-selected' : 'transparent'}
      isActive={isSelected}
      isDisabled={isDefinitivelyBlocked}
      onClick={() => history.push(routes.getCardPath(creditCardId))}
      padding="space-16"
    >
      <SunshineCard.Slot name="aside">
        <Flex gap="space-16" width="40%">
          <Image
            alt=""
            boxSize="40px"
            filter={isCreditCardPendingOrder(creditCard) ? 'grayscale(1)' : ''}
            objectFit="contain"
            opacity={isCreditCardPendingOrder(creditCard) ? 0.5 : 1}
            src={cardAssetUrl}
          />

          <Stack spacing="space-0">
            <Text fontWeight="weight-500" noOfLines={1} variant="primary">
              {cardName}
            </Text>

            {cardDescription ? <Text size="sm">{cardDescription}</Text> : null}
          </Stack>
        </Flex>
      </SunshineCard.Slot>

      {!isCreditCardPendingOrder(creditCard) ? (
        <SunshineCard.Content>
          <Stack
            alignItems="flex-start"
            flex={1}
            flexDirection="column"
            justifyContent="center"
            spacing="space-8"
          >
            <PaymentCardLimitsSkeleton
              isLoaded={!loading}
              width={loading ? '116px' : 'auto'}
            >
              <Text fontWeight="weight-500" size="xs" variant="primary">
                {locales.formatString(locales.limitsDescription, {
                  amount: formatAmount({
                    forcePlusSign: false,
                    value: totalPayment,
                  }),
                  maxAmount: formatAmount({
                    forcePlusSign: false,
                    value: limitPayment,
                  }),
                })}
              </Text>
            </PaymentCardLimitsSkeleton>

            <PaymentCardLimitsSkeleton isLoaded={!loading} width="175px">
              <ProgressBar
                marginBottom="space-6"
                marginTop="-space-6"
                ratio={limitPayment === 0 ? 0 : totalPayment / limitPayment}
              />

              <Text marginTop="-space-2" size="xs">
                {locales.paymentLimit}
              </Text>
            </PaymentCardLimitsSkeleton>
          </Stack>
        </SunshineCard.Content>
      ) : null}

      <SunshineCard.Slot name="action">
        <PaymentCardStatusBadge creditCard={creditCard} />
      </SunshineCard.Slot>
    </SunshineCard>
  );
};

export default PaymentCardRow;
